import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { csrfFromFlow, firstFlowMessage } from "../../auth/kratosFlow.js";
import { productLogout } from "../../hooks/useProductMe.js";
import Button from "../ui/Button.jsx";

const panelStyle = {
  border: "1px solid var(--border-bright)",
  borderRadius: "8px",
  padding: "20px",
  marginBottom: "20px",
  background: "var(--bg-surface)",
};

/**
 * @param {{ user: { email?: string, name?: { first?: string, last?: string } } | null }} props
 */
export default function MyProfileSection({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const flowFromUrl = searchParams.get("flow");
  const fromRecovery = Boolean(location.state?.fromRecovery) || Boolean(flowFromUrl?.trim());

  const [flow, setFlow] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);

  const displayName =
    [user?.name?.first, user?.name?.last].filter(Boolean).join(" ") || user?.email || "—";

  const loadSettingsFlow = useCallback(async () => {
    setError("");
    const url =
      flowFromUrl != null && flowFromUrl.trim() !== ""
        ? `/api/auth/settings/init?flow=${encodeURIComponent(flowFromUrl.trim())}`
        : "/api/auth/settings/init";
    const res = await fetch(url, { credentials: "include" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data?.error?.message || firstFlowMessage(data) || "Could not open password settings");
      return;
    }
    setFlow(data);
  }, [flowFromUrl]);

  useEffect(() => {
    loadSettingsFlow();
  }, [loadSettingsFlow]);

  useEffect(() => {
    if (location.hash === "#change-password") {
      const el = document.getElementById("change-password");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash]);

  const onChangePassword = async (e) => {
    e.preventDefault();
    if (!flow?.id) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const res = await fetch("/api/auth/settings/password", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          flow_id: flow.id,
          csrf_token: csrfFromFlow(flow),
          password,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(firstFlowMessage(data) || "Password update failed");
        if (data?.id) setFlow(data);
        else await loadSettingsFlow();
        return;
      }
      if (fromRecovery) {
        navigate("/login", { replace: true, state: { recovered: true } });
        return;
      }
      setSuccess("Password updated.");
      setPassword("");
      await loadSettingsFlow();
    } catch {
      setError("Request failed");
    } finally {
      setBusy(false);
    }
  };

  const onSignOut = async () => {
    await productLogout();
    navigate("/login", { replace: true });
  };

  return (
    <div>
      <h2 style={{ fontSize: "18px", fontWeight: 600, margin: "0 0 16px 0" }}>
        {fromRecovery ? "Set new password" : "My Profile"}
      </h2>

      {!fromRecovery ? (
        <>
          <div style={panelStyle}>
            <p className="text-secondary" style={{ fontSize: "13px", margin: "0 0 16px 0" }}>
              Profile fields are read-only. Contact an administrator to change your email or role.
            </p>
            <div style={{ fontSize: "13px", marginBottom: "8px" }}>
              <span className="text-secondary">Name: </span>
              {displayName}
            </div>
            <div style={{ fontSize: "13px" }}>
              <span className="text-secondary">Email: </span>
              {user?.email || "—"}
            </div>
          </div>
        </>
      ) : (
        <p className="text-secondary" style={{ fontSize: "14px", marginBottom: "16px" }}>
          Choose a new password to finish account recovery.
        </p>
      )}

      <div id="change-password" style={panelStyle}>
        <h3 style={{ fontSize: "15px", fontWeight: 600, margin: "0 0 12px 0" }}>Change password</h3>
        {error ? <p style={{ color: "#f87171", fontSize: "13px", marginTop: 0 }}>{error}</p> : null}
        {success ? <p style={{ color: "var(--accent)", fontSize: "13px", marginTop: 0 }}>{success}</p> : null}
        <form onSubmit={onChangePassword}>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            placeholder="New password"
            value={password}
            onChange={(ev) => setPassword(ev.target.value)}
            className="w-full"
            style={{
              padding: "10px 12px",
              marginBottom: "12px",
              borderRadius: "6px",
              border: "1px solid var(--border-bright)",
              background: "var(--bg-base)",
              color: "var(--text-primary)",
              width: "100%",
              maxWidth: "360px",
            }}
          />
          <Button type="submit" variant="primary" size="md" disabled={busy || !flow?.id}>
            {busy ? "Updating…" : "Update password"}
          </Button>
        </form>
      </div>

      {!fromRecovery ? (
        <Button type="button" variant="ghost" size="md" onClick={onSignOut}>
          Sign out
        </Button>
      ) : (
        <Link to="/login" style={{ fontSize: "13px", color: "var(--accent)", textDecoration: "none" }}>
          ← Back to sign in
        </Link>
      )}
    </div>
  );
}
